import FunnelAggregation from '@/reports/funnel/funnel.aggregation';
import FunnelCsvAggregation from '@/reports/funnel/funnel-csv.aggregation';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import defaults from 'lodash/defaults';
import last from 'lodash/last';
import zip from 'lodash/zip';
import filter from 'lodash/filter';
import uniq from 'lodash/uniq';
import moment from '@/utils/moment-wrapper';
import values from 'lodash/values';
import intersection from 'lodash/intersection';
import isPlainObject from 'lodash/isPlainObject';
import { ReportError, buildReportStream, getDefaultTimeseries, isStale, getMobileProperties } from '@/reports/utils';
import { isDefaultSegment, getSegmentName, parseSegmentParams } from '@/utils/segments.js';
import { connectAggregationToObservable, getRows } from '@pendo/agg-connect';
import { FUNNEL_CHART_COLORS } from '@/common/chart/chart-colors';
import { MetadataTypeId, FilterType } from '@/types/analytics/analytics.types';
import { Subject, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { LOADING_STATES } from '@/constants';
import { iterateThroughMetadata } from '@/vuex/modules/metadata/metadata.utils';
import { VanillaDateRange } from '@/data/vanilla-date-range';

export const TABLE_TYPES = {
    VISITOR: 'Visitor',
    SUMMARY: 'Summary',
    STEP: 'Step',
};

export const VISITOR_ROW_LIMIT = 10000;

export const MAX_CARDINALITY_LIMIT = 10000;

export const FUNNEL_PARAMETERS_KEY = '_pendo_funnel_parameters';

function allResourcesExist (id) {
    return store.getters['savedReports/funnels/allResourcesExist'](id);
}

function getFunnelResources (funnel) {
    if (funnel.id) return store.getters['savedReports/funnels/resources'](funnel.id);

    return getUnsavedFunnelResources(funnel);
}

function getAppMap () {
    return store.state.apps.map;
}

function loadFunnelResources (id) {
    return store.dispatch('savedReports/funnels/loadResources', { id });
}

function getSegmentById (segmentId) {
    return store.getters['segments/segmentById'](segmentId);
}

function getUnsavedFunnelResources (funnel) {
    const items = get(funnel, 'definition.config.items', []);

    return items.map((item) => {
        const { pageId, featureId, trackTypeId } = item;
        if (pageId) return store.getters['pages/pageById'](pageId);
        if (featureId) return store.getters['features/featureById'](featureId);
        if (trackTypeId) return store.getters['trackTypes/trackTypeById'](trackTypeId);

        return null;
    });
}

export function buildAggParams (funnel) {
    const { definition } = funnel;
    const { config, groupings } = definition;
    const vanillaDateRange = new VanillaDateRange(definition.timeSeries);
    const timeSeries = vanillaDateRange.timeSeries();

    return {
        config: parseSegmentParams(config),
        timeSeries,
        groupings,
    };
}

export function compileAggregation (funnel) {
    const params = buildAggParams(funnel);

    return FunnelAggregation.build(params);
}

async function allFunnelItemsExist (funnel) {
    const { id } = funnel;
    try {
        if (allResourcesExist(id)) return true;

        await loadFunnelResources(id);

        return allResourcesExist(id);
    } catch {
        return false;
    }
}

async function validate (funnel) {
    const runnable = await allFunnelItemsExist(funnel);
    if (!runnable) {
        throw new ReportError({
            message:
                'We are unable to run your funnel because we could not find some of the pages, features, or track events. Please edit the funnel and correct the missing pages, features, or track events, or delete the funnel if you no longer need it.',
            retriable: false,
        });
    }
}

export function buildTimeSeriesString (timeSeries, preposition) {
    let dateRangeString = '';
    const { start, end, type } = timeSeries;
    const isOneDay = moment(start).isSame(moment(end).subtract(1, 'days'), 'day');
    if (type !== 'Custom') {
        dateRangeString = type.toLowerCase();
        if (type !== 'Today' && type !== 'Yesterday') {
            dateRangeString = preposition ? `over ${dateRangeString}` : dateRangeString;
        }
    } else if (isOneDay) {
        if (moment().isSame(moment(start), 'day')) {
            dateRangeString = 'today';
        } else if (
            moment()
                .subtract(1, 'days')
                .isSame(moment(start), 'day')
        ) {
            dateRangeString = 'yesterday';
        } else {
            dateRangeString = preposition
                ? `on ${moment(start, { ignoreTimeZone: true }).format('ll')}`
                : moment(start, { ignoreTimeZone: true }).format('ll');
        }
    } else {
        dateRangeString = preposition
            ? `over ${moment(start, { ignoreTimeZone: true }).format('ll')} - ${moment(end, {
                ignoreTimeZone: true,
            })
                .subtract(1, 'days')
                .format('ll')}`
            : `${moment(start, { ignoreTimeZone: true }).format('ll')} - ${moment(end, {
                ignoreTimeZone: true,
            })
                .subtract(1, 'days')
                .format('ll')}`;
    }

    return dateRangeString;
}

export function buildResolvedFunnelState (uniqueVisitorFunnel, savedVisibleGroups, value, status, tableBreakdown = '') {
    return {
        status,
        percentComplete: 100,
        steps: value.steps,
        isShowingDropoff: false,
        apps: value.apps,
        segment: value.segment || {},
        visitors: Object.freeze(value.visitors),
        wholeFunnelMetrics: value.wholeFunnelMetrics,
        funnelType: uniqueVisitorFunnel ? 'uniqueVisitors' : 'totalAttempts',
        selectedStep: undefined,
        visibleGroups: savedVisibleGroups
            ? intersection(savedVisibleGroups, [...value.steps.keys()])
            : [...value.steps.keys()],
        selectedGroup: undefined,
        resources: value.resources,
        sessionReplay: undefined,
        tableBreakdown,
        funnelExceedsCardinality: value.funnelExceedsCardinality,
    };
}

function getAppsInFunnel (funnel) {
    const resources = getFunnelResources(funnel);
    const colorsContinued = [...FUNNEL_CHART_COLORS.continued];
    const appMap = getAppMap();
    let numOfApps = 0;
    const appsInFunnel = {};
    resources.forEach((resource) => {
        const appId = get(resource, 'appId');
        if (!appsInFunnel[appId] && appMap[appId]) {
            const app = appMap[appId];
            const appColor = colorsContinued[numOfApps % colorsContinued.length];
            appsInFunnel[appId] = {
                id: appId,
                displayName: app.displayName,
                platform: app.platform,
                colors: [appColor],
            };
            numOfApps++;
        }
    });

    return appsInFunnel;
}

function fillIncompleteFunnel (incompleteStepsList) {
    // if n steps have identical `count` vals then
    // the result only contains the last
    // so we need to build out a list of all steps
    const lastStep = last(incompleteStepsList);
    const allSteps = [];
    let curIndex = incompleteStepsList.length - 2;
    if (lastStep) {
        allSteps.push(lastStep);
        for (let i = lastStep.steps - 1; i > 0; i--) {
            if (curIndex > -1 && incompleteStepsList[curIndex].steps === i) {
                allSteps.unshift(incompleteStepsList[curIndex]);
                curIndex--;
            } else {
                allSteps.unshift({
                    count: incompleteStepsList[curIndex + 1].count,
                    steps: i,
                    groupValue0: incompleteStepsList[curIndex + 1].groupValue0,
                });
            }
        }
    }

    return allSteps;
}

function buildGroup (funnel, group, groupByValue = 'ungrouped') {
    const resources = getFunnelResources(funnel);
    const items = get(funnel, 'definition.config.items');
    const steps = group.groupSteps;

    return zip(steps, resources, items).map(([step, resource, item], i) => {
        let prevStep = {};
        if (i !== 0) {
            const prev = steps[i - 1];
            prevStep = {
                appIdFromPrevious: get(resources, `[${i - 1}].appId`),
                resourceIdFromPrevious: get(resources, `[${i - 1}].id`),
                resourceKindFromPrevious: get(resources, `[${i - 1}].kind`),
                averageTimeFromPrevious: get(group, `averageTimes[${i - 1}]`),
                medianTimeFromPrevious: get(group, `medianTimes[${i - 1}]`),
                prevCount: prev?.count || 0,
            };
        }

        let name = get(resource, 'name', 'Unknown step');
        if (item.guideEventTypes) {
            const guideStepIndex = resource.steps.map(({ id }) => id).indexOf(item.guideStepId);
            switch (item.guideEventTypes) {
                case 'guideSeen':
                    name += item.guideStepId ? ` - Seen at Step ${guideStepIndex + 1}` : ' - Seen at any step';
                    break;
                case 'guideDismissed':
                    name += item.guideStepId
                        ? ` - Dismissed at Step ${guideStepIndex + 1}`
                        : ' - Dismissed at any step';
                    break;
                case 'guideNotSeen':
                    name += ' - Not seen (control group)';
            }
        }

        const config = {
            groupByValue,
            resource,
            name,
            appId: get(resource, 'appId'),
            prevStep,
            steps: i + 1,
            averageTimeToNextStep: get(group, `averageTimes[${i}]`),
            medianTimeToNextStep: get(group, `medianTimes[${i}]`),
            count: step?.count || 0,
            averageTimeToCompletion: get(group, 'averageTimeToCompletion'),
            medianTimeToCompletion: get(group, 'medianTimeToCompletion'),
            percentCompleted: get(group, 'percentCompleted'),
        };

        return config;
    });
}

function processResponse (funnel, rows) {
    const groupRows = defaults(
        groupBy(rows, ({ rowType }) => rowType),
        {
            visitors: [],
            wholeFunnelMetrics: [],
            groupInfo: [],
            funnelExceedsCardinality: [],
        },
    );
    const { visitors, wholeFunnelMetrics, groupInfo, funnelExceedsCardinality } = groupRows;
    const appsInFunnel = getAppsInFunnel(funnel);
    const segmentId = get(funnel, 'definition.config.segmentId', 'everyone');
    const segment = getSegmentById(segmentId);

    const groupInfoMap = new Map();
    for (const group of groupInfo) {
        const key = group.groupValue0 || 'ungrouped';
        const savedGroupByKey = groupInfoMap.has(key);

        const groupSteps = group.groupSteps || [];
        const sortedGroupSteps = groupSteps.sort((a, b) => a.steps - b.steps);
        const completedGroupSteps = fillIncompleteFunnel(sortedGroupSteps);

        const fixedGroup = {
            ...group,
            groupSteps: completedGroupSteps,
        };

        const builtGroup = buildGroup(funnel, fixedGroup, key);

        if (!savedGroupByKey) {
            groupInfoMap.set(key, builtGroup);
        }
    }

    const groupInfoArray = Array.from(groupInfoMap);
    // Assign colors to steps
    const colorsContinued = [...FUNNEL_CHART_COLORS.continued];
    const colorsDropped = [...FUNNEL_CHART_COLORS.dropped];
    const colorsHover = [...FUNNEL_CHART_COLORS.hover];
    const colorsDroppedHover = [...FUNNEL_CHART_COLORS.droppedHover];
    const colorsSelected = [...FUNNEL_CHART_COLORS.selected];
    const colorsDroppedSelected = [...FUNNEL_CHART_COLORS.droppedSelected];
    if (get(funnel, 'definition.groupings', []).length) {
        groupInfoArray.forEach((stepGroup, idx) => {
            stepGroup[1].forEach((step) => {
                step.colorContinued = colorsContinued[idx % colorsContinued.length];
                step.colorDropped = colorsDropped[idx % colorsDropped.length];
                step.colorHover = colorsHover[idx % colorsHover.length];
                step.colorDroppedHover = colorsDroppedHover[idx % colorsDroppedHover.length];
                step.colorSelected = colorsSelected[idx % colorsSelected.length];
                step.colorDroppedSelected = colorsDroppedSelected[idx % colorsDroppedSelected.length];
            });
        });
    } else {
        const appIds = Object.keys(appsInFunnel);
        get(groupInfoArray, '[0][1]', []).forEach((step) => {
            const idx = appIds.findIndex((id) => id === step.appId.toString());
            step.colorContinued = colorsContinued[idx % colorsContinued.length];
            step.colorDropped = colorsDropped[idx % colorsDropped.length];
            step.colorHover = colorsHover[idx % colorsHover.length];
            step.colorDroppedHover = colorsDroppedHover[idx % colorsDroppedHover.length];
            step.colorSelected = colorsSelected[idx % colorsSelected.length];
            step.colorDroppedSelected = colorsDroppedSelected[idx % colorsDroppedSelected.length];
        });
    }

    return {
        visitors,
        steps: new Map(groupInfoArray),
        wholeFunnelMetrics: wholeFunnelMetrics[0] || {},
        segment,
        apps: values(appsInFunnel),
        resources: getFunnelResources(funnel),
        funnelExceedsCardinality: get(funnelExceedsCardinality[0], 'hasExceededMaxThresholdCount', false),
    };
}

export function createFunnelStream () {
    return buildReportStream({
        aggregation: FunnelAggregation,
        validate,
        buildAggParams,
        processResponse,
    });
}

export function createUnsavedFunnelStream (funnel) {
    const subject = new Subject();
    const aggregation = FunnelAggregation;
    delete aggregation.requestId;
    delete aggregation.response;
    const response$ = connectAggregationToObservable(
        aggregation,
        subject.pipe(
            mergeMap(async ([funnel]) => {
                return {
                    ...buildAggParams(funnel),
                };
            }),
        ),
    ).pipe(
        mergeMap(async (response) => {
            const { LOADING, NOTIFICATION, REJECTED, RESOLVED } = LOADING_STATES;
            let data;
            switch (response.status) {
                case LOADING:
                    return response;
                case NOTIFICATION:
                    return response;
                case REJECTED:
                    return response.error;
                case RESOLVED:
                    data = getRows(response.value);

                    return {
                        status: response.status,
                        value: processResponse(funnel, data),
                    };
            }
        }),
    );

    return [response$, subject];
}

export function fetchCsv (funnel) {
    return connectAggregationToObservable(
        FunnelCsvAggregation,
        of({
            gcsFileName: funnel.lastSuccessRunObj,
            resources: getFunnelResources(funnel),
        }),
    );
}

// These funnel versions indicate which features are currently in use
export const FUNNEL_VERSION = 5;

export const FUNNEL_VERSIONS = {
    0: 'classicFunnel',
    1: 'wholeFunnel',
    2: 'uniqueVisitorFunnel',
    3: 'groupByFunnel',
    4: 'sessionReplayFunnel',
    5: 'aggGroupInfoFunnel',
};

export function getFunnelVersion () {
    const version = FUNNEL_VERSION;

    return {
        version,
        versionName: FUNNEL_VERSIONS[version],
    };
}

export function checkValidFilterType (items, validTypes) {
    return items.some((item) => item.filters?.some(({ filterType }) => validTypes.includes(filterType)));
}

export function checkValidGroupType (groupings, validTypes) {
    return groupings?.some(({ type, configuration }) => {
        if (type === MetadataTypeId.Event) {
            return validTypes.includes(configuration.kind);
        }

        return validTypes.includes(type);
    });
}

export async function trackFunnelReport ({
    funnelResource,
    funnelState,
    trackType,
    location = 'report',
    isAuto = false,
    fromWebAnalytics = false,
    fromAiAgents = false,
}) {
    const { definition, id } = funnelResource;
    const { config, version, timeSeries, groupings } = definition;
    const { items, appId, segmentId, uniqueVisitorFunnel } = config;

    const dateRangeStart = moment(timeSeries.start);
    const dateRangeEnd = moment(timeSeries.end);
    const dateRangeDays = dateRangeEnd.diff(dateRangeStart, 'days');

    const numFilters = items.reduce((count, item) => {
        return count + (item.filters?.length ?? 0);
    }, 0);

    const hasNonHistoricalMetadata = checkValidGroupType(groupings, [
        MetadataTypeId.Visitor,
        MetadataTypeId.Account,
        MetadataTypeId.ParentAccount,
    ]);
    const hasHistoricalMetadata =
        checkValidFilterType(items, [FilterType.HistoricalMetadata]) ||
        checkValidGroupType(groupings, [MetadataTypeId.Historical]);
    const hasEventProperties =
        checkValidFilterType(items, [FilterType.Custom]) || checkValidGroupType(groupings, [MetadataTypeId.Event]);
    const hasDeviceProps =
        checkValidFilterType(items, [FilterType.Device]) || checkValidGroupType(groupings, [MetadataTypeId.Automatic]);
    const hasGuideEvents = Boolean(filter(items, 'guideId').length);

    const appIds = appId.map((appId) => {
        return { appId };
    });
    const trackMobileProperties = getMobileProperties(appIds);

    const hasSessionPropertiesFilters = items?.some((item) =>
        item.filters?.some((filter) => filter.filterType === FilterType.Utm),
    );

    const hasSessionPropertiesGroups = groupings?.some((item) => item.type === FilterType.Utm);

    let trackEventData = {
        reportId: id,
        location,
        numberOfFunnelSteps: items.length,
        features: filter(items, 'featureId').length,
        trackEvents: filter(items, 'trackTypeId').length,
        pages: filter(items, 'pageId').length,
        guides: filter(items, 'guideId').length,
        aiAgents: filter(items, 'agentId').length,
        hasAIEvents: filter(items, 'agentId').length > 0,
        numberOfApps: appId.length,
        dateRangeDays,
        numFilters,
        hasFilters: numFilters > 0,
        hasGroups: groupings?.length > 0,
        dateRangeSelection: timeSeries.type,
        segment: isDefaultSegment(segmentId) ? 'default' : 'custom',
        segmentName: await getSegmentName(segmentId),
        analyzeBy: uniqueVisitorFunnel ? 'uniqueVisitors' : 'totalAttempts',
        funnelRunType: trackType,
        funnelVersion: version,
        hasNonHistoricalMetadata,
        hasHistoricalMetadata,
        hasEventProperties,
        hasDeviceProps,
        hasGuideEvents,
        funnelVersionName: FUNNEL_VERSIONS[version],
        ...trackMobileProperties,
        has_session_properties: hasSessionPropertiesFilters || hasSessionPropertiesGroups,
        from_web_analytics: fromWebAnalytics,
        from_ai_agents: fromAiAgents,
    };

    if (trackType === 'resolved') {
        const { visitors, reportStartTime } = funnelState;
        const uniqueGroups = uniq(visitors.map(({ groupValue0 }) => groupValue0));

        trackEventData = {
            ...trackEventData,
            visitorCount: visitors.length,
            groupCount: uniqueGroups.length,
            funnelLoadTime: Date.now() - reportStartTime,
        };

        window.pendo.track('Funnel Report Complete', trackEventData);
    } else {
        trackEventData = {
            ...trackEventData,
            isAuto,
            isStale: isAuto && trackType !== 'run' ? isStale(funnelResource) : false,
        };

        window.pendo.track('Run Funnel Report', trackEventData);
    }
}

export function buildDefaultFunnel () {
    return {
        name: 'New Funnel',
        shared: true,
        type: 'Funnel',
        definition: {
            type: 'Funnel', // keeping for backwards compat with old funnels code
            config: {
                maxDuration: 0,
                maxInterval: 0,
                items: [{}, {}],
                appId: [],
                uniqueVisitorFunnel: true,
                segmentId: 'everyone',
                mapAnonymous: true,
                onlyMatchedEvents: true,
            },
            groupings: [],
            timeSeries: getDefaultTimeseries(),
            history: [],
            version: getFunnelVersion().version,
        },
    };
}

export function ensureCompleteFunnelConfig (funnelConfig) {
    const { definition: defaultFunnelConfig } = buildDefaultFunnel();

    const iterate = (iterateObj, comparisonObj) => {
        const comparisonObjCopy = comparisonObj;
        Object.keys(iterateObj).forEach((key) => {
            // Check for matches
            if (Object.hasOwn(comparisonObjCopy, key)) {
                // If the key is an object value, iterate through that one first
                if (isPlainObject(iterateObj[key])) {
                    const completedObject = iterate(iterateObj[key], comparisonObjCopy[key]);
                    comparisonObjCopy[key] = completedObject;
                }

                return;
            }

            // If no matches, we need to initialize the value with the default config's
            comparisonObjCopy[key] = iterateObj[key];
        });

        return comparisonObjCopy;
    };

    const completeFunnelConfig = iterate(defaultFunnelConfig, funnelConfig);

    return completeFunnelConfig;
}

export function checkIfGroupingsExceedCardinality ({ groupings, metadata, historicalMetadata }) {
    if (groupings?.length) {
        return groupings.some(({ configuration, type }) => {
            const { kind, group, field } = configuration;
            const groupingId = `${kind}.${group}.${field}`;

            if (type === MetadataTypeId.Historical) {
                return historicalMetadata.some(
                    ({ id, kind: Kind, group: Group, field: Field, cardinalityEstimate }) => {
                        const metadataId = id || `${Kind}.${Group}.${Field}`;

                        return metadataId === groupingId && cardinalityEstimate > MAX_CARDINALITY_LIMIT;
                    },
                );
            }

            if (type === MetadataTypeId.Event && kind !== MetadataTypeId.Automatic) {
                const metadataByType = get(metadata, type, []);
                const metadataList = iterateThroughMetadata(metadataByType, type);
                const validMetadataTypes = ['string', 'integer', 'float'];

                return metadataList.some(
                    ({ id, kind: Kind, group: Group, field: Field, Type, cardinalityEstimate }) => {
                        const metadataId = id || `${Kind}.${Group}.${Field}`;

                        return (
                            Group !== 'pendo' &&
                            validMetadataTypes.includes(Type) &&
                            metadataId === groupingId &&
                            cardinalityEstimate > MAX_CARDINALITY_LIMIT
                        );
                    },
                );
            }

            return false;
        });
    }

    return false;
}
